export type ProjectCategory = "properties" | "products" | "courses" | "packages" | "plans" | "custom";

export interface CategoryConfig {
  key: ProjectCategory;
  icon: string;
  title: string;
  subtitle: string;
  inventoryLabel: string;
  statusOptions: { value: string; badge: string }[];
  formFields: { key: string; label: string; type: "text" | "number" | "date" | "radio"; options?: string[]; placeholder?: string }[];
  inventoryColumns: { key: string; label: string }[];
  itemFields: { key: string; label: string; type: "text" | "number" | "date" | "dropdown" | "tags"; options?: string[]; required?: boolean; placeholder?: string }[];
  priceLabel: string;
}

export const categoryConfigs: Record<ProjectCategory, CategoryConfig> = {
  properties: {
    key: "properties",
    icon: "ri-building-line",
    title: "Properties",
    subtitle: "Real Estate, Housing Projects",
    inventoryLabel: "Units",
    statusOptions: [
      { value: "Available", badge: "bg-green-50 text-green-700" },
      { value: "Booked", badge: "bg-indigo-50 text-indigo-700" },
      { value: "Sold", badge: "bg-gray-100 text-gray-600" },
      { value: "On Hold", badge: "bg-amber-50 text-amber-700" },
    ],
    formFields: [
      { key: "location", label: "Location", type: "text", placeholder: "e.g. Bandra West, Mumbai" },
      { key: "priceFrom", label: "Price From (₹)", type: "text", placeholder: "e.g. 1.8 Cr" },
      { key: "priceTo", label: "Price To (₹)", type: "text", placeholder: "e.g. 2.4 Cr" },
      { key: "totalUnits", label: "Total Units", type: "number", placeholder: "e.g. 48" },
      { key: "reraNumber", label: "RERA Number", type: "text", placeholder: "e.g. P51800045678" },
      { key: "possession", label: "Possession Date", type: "text", placeholder: "e.g. December 2026" },
    ],
    inventoryColumns: [
      { key: "unitNo", label: "Unit No" },
      { key: "type", label: "Type" },
      { key: "floor", label: "Floor" },
      { key: "area", label: "Area (sqft)" },
      { key: "price", label: "Price" },
      { key: "status", label: "Status" },
      { key: "buyer", label: "Buyer" },
    ],
    itemFields: [
      { key: "unitNo", label: "Unit Number", type: "text", required: true, placeholder: "e.g. A-101" },
      { key: "type", label: "Type", type: "dropdown", options: ["2BHK", "3BHK", "Villa", "Plot", "Commercial"], required: true },
      { key: "floor", label: "Floor", type: "text", placeholder: "e.g. 1st" },
      { key: "area", label: "Area (sqft)", type: "number", placeholder: "e.g. 950" },
      { key: "price", label: "Price ₹", type: "text", required: true, placeholder: "e.g. 1.8 Cr" },
      { key: "status", label: "Status", type: "dropdown", options: ["Available", "Booked", "Sold", "On Hold"] },
    ],
    priceLabel: "Price Range",
  },
  products: {
    key: "products",
    icon: "ri-shopping-bag-line",
    title: "Products",
    subtitle: "Manufacturing, Retail, Trading",
    inventoryLabel: "Products / SKUs",
    statusOptions: [
      { value: "In Stock", badge: "bg-green-50 text-green-700" },
      { value: "Low Stock", badge: "bg-amber-50 text-amber-700" },
      { value: "Out of Stock", badge: "bg-red-50 text-red-700" },
      { value: "Discontinued", badge: "bg-gray-100 text-gray-600" },
    ],
    formFields: [
      { key: "productCategory", label: "Product Category", type: "text", placeholder: "e.g. Industrial Equipment" },
      { key: "totalSKUs", label: "Total SKUs", type: "number", placeholder: "e.g. 8" },
      { key: "priceFrom", label: "Price From (₹)", type: "text", placeholder: "e.g. 45000" },
      { key: "priceTo", label: "Price To (₹)", type: "text", placeholder: "e.g. 280000" },
      { key: "moq", label: "MOQ - Minimum Order Qty", type: "number", placeholder: "e.g. 5" },
      { key: "leadTime", label: "Lead Time", type: "text", placeholder: "e.g. 7-10 days" },
    ],
    inventoryColumns: [
      { key: "sku", label: "SKU" },
      { key: "productName", label: "Product Name" },
      { key: "category", label: "Category" },
      { key: "stockQty", label: "Stock Qty" },
      { key: "unitPrice", label: "Unit Price" },
      { key: "status", label: "Status" },
    ],
    itemFields: [
      { key: "sku", label: "SKU", type: "text", required: true, placeholder: "e.g. IPX-450-A" },
      { key: "productName", label: "Product Name", type: "text", required: true, placeholder: "e.g. Industrial Pump X450" },
      { key: "category", label: "Category", type: "text", placeholder: "e.g. Centrifugal Pump" },
      { key: "stockQty", label: "Stock Quantity", type: "number", required: true, placeholder: "e.g. 120" },
      { key: "unitPrice", label: "Unit Price ₹", type: "text", required: true, placeholder: "e.g. 45000" },
      { key: "status", label: "Status", type: "dropdown", options: ["In Stock", "Low Stock", "Out of Stock", "Discontinued"] },
      { key: "notes", label: "Notes", type: "text", placeholder: "Any additional info" },
    ],
    priceLabel: "Price Range",
  },
  courses: {
    key: "courses",
    icon: "ri-graduation-cap-line",
    title: "Courses & Batches",
    subtitle: "Education, Training, Coaching",
    inventoryLabel: "Seats / Batches",
    statusOptions: [
      { value: "Available", badge: "bg-green-50 text-green-700" },
      { value: "Enrolled", badge: "bg-indigo-50 text-indigo-700" },
      { value: "Cancelled", badge: "bg-red-50 text-red-700" },
      { value: "Waitlist", badge: "bg-amber-50 text-amber-700" },
    ],
    formFields: [
      { key: "courseName", label: "Course/Batch Name", type: "text", placeholder: "e.g. MBA Batch July 2026" },
      { key: "totalSeats", label: "Total Seats", type: "number", placeholder: "e.g. 60" },
      { key: "fee", label: "Fee (₹)", type: "text", placeholder: "e.g. 240000" },
      { key: "startDate", label: "Start Date", type: "date", placeholder: "" },
      { key: "endDate", label: "End Date", type: "date", placeholder: "" },
      { key: "mode", label: "Mode", type: "radio", options: ["Online", "Offline", "Hybrid"] },
    ],
    inventoryColumns: [
      { key: "batchNo", label: "Batch No" },
      { key: "course", label: "Course" },
      { key: "mode", label: "Mode" },
      { key: "seats", label: "Seats" },
      { key: "enrolled", label: "Enrolled" },
      { key: "fee", label: "Fee" },
      { key: "startDate", label: "Start Date" },
      { key: "status", label: "Status" },
    ],
    itemFields: [
      { key: "batchNo", label: "Batch Number", type: "text", required: true, placeholder: "e.g. MBA-JUL-01" },
      { key: "course", label: "Course Name", type: "text", required: true, placeholder: "e.g. MBA General Management" },
      { key: "mode", label: "Mode", type: "dropdown", options: ["Online", "Offline", "Hybrid"], required: true },
      { key: "seats", label: "Total Seats", type: "number", required: true, placeholder: "e.g. 30" },
      { key: "fee", label: "Fee ₹", type: "text", required: true, placeholder: "e.g. 240000" },
      { key: "startDate", label: "Start Date", type: "date" },
      { key: "endDate", label: "End Date", type: "date" },
      { key: "status", label: "Status", type: "dropdown", options: ["Available", "Enrolled", "Cancelled", "Waitlist"] },
    ],
    priceLabel: "Fee",
  },
  packages: {
    key: "packages",
    icon: "ri-heart-pulse-line",
    title: "Packages & Services",
    subtitle: "Healthcare, Wellness, Consulting",
    inventoryLabel: "Packages",
    statusOptions: [
      { value: "Available", badge: "bg-green-50 text-green-700" },
      { value: "Booked", badge: "bg-indigo-50 text-indigo-700" },
      { value: "Completed", badge: "bg-gray-100 text-gray-600" },
      { value: "Discontinued", badge: "bg-red-50 text-red-700" },
    ],
    formFields: [
      { key: "serviceCategory", label: "Service Category", type: "text", placeholder: "e.g. Health Checkup" },
      { key: "priceFrom", label: "Price From (₹)", type: "text", placeholder: "e.g. 1500" },
      { key: "priceTo", label: "Price To (₹)", type: "text", placeholder: "e.g. 25000" },
      { key: "duration", label: "Duration", type: "text", placeholder: "e.g. 45 mins" },
      { key: "slotsAvailable", label: "Slots Available", type: "number", placeholder: "e.g. 120" },
      { key: "validity", label: "Validity", type: "text", placeholder: "e.g. 1 year" },
    ],
    inventoryColumns: [
      { key: "packageName", label: "Package" },
      { key: "duration", label: "Duration" },
      { key: "price", label: "Price" },
      { key: "slotsAvailable", label: "Slots Available" },
      { key: "status", label: "Status" },
    ],
    itemFields: [
      { key: "packageName", label: "Package Name", type: "text", required: true, placeholder: "e.g. Full Body Health Check" },
      { key: "duration", label: "Duration", type: "text", placeholder: "e.g. 60 mins" },
      { key: "price", label: "Price ₹", type: "text", required: true, placeholder: "e.g. 5000" },
      { key: "slotsAvailable", label: "Slots Available", type: "number", placeholder: "e.g. 20" },
      { key: "validity", label: "Validity", type: "text", placeholder: "e.g. 6 months" },
      { key: "status", label: "Status", type: "dropdown", options: ["Available", "Booked", "Completed", "Discontinued"] },
      { key: "notes", label: "Notes", type: "text", placeholder: "Additional details" },
    ],
    priceLabel: "Price Range",
  },
  plans: {
    key: "plans",
    icon: "ri-mac-line",
    title: "Plans & Licenses",
    subtitle: "SaaS, IT Services, Software",
    inventoryLabel: "Plans / Licenses",
    statusOptions: [
      { value: "Active", badge: "bg-green-50 text-green-700" },
      { value: "Inactive", badge: "bg-gray-100 text-gray-600" },
      { value: "Coming Soon", badge: "bg-blue-50 text-blue-700" },
      { value: "Discontinued", badge: "bg-red-50 text-red-700" },
    ],
    formFields: [
      { key: "planTier", label: "Plan Tier", type: "text", placeholder: "e.g. Basic, Professional, Enterprise" },
      { key: "priceMonth", label: "Price/month (₹)", type: "text", placeholder: "e.g. 999" },
      { key: "priceYear", label: "Price/year (₹)", type: "text", placeholder: "e.g. 9999" },
      { key: "maxUsers", label: "Max Users", type: "number", placeholder: "e.g. 50" },
      { key: "trialDays", label: "Trial Period (Days)", type: "number", placeholder: "e.g. 14" },
    ],
    inventoryColumns: [
      { key: "planName", label: "Plan Name" },
      { key: "priceMonth", label: "Price/month" },
      { key: "priceYear", label: "Price/year" },
      { key: "maxUsers", label: "Max Users" },
      { key: "activeClients", label: "Active Clients" },
      { key: "status", label: "Status" },
    ],
    itemFields: [
      { key: "planName", label: "Plan Name", type: "text", required: true, placeholder: "e.g. Professional" },
      { key: "priceMonth", label: "Price/month ₹", type: "text", required: true, placeholder: "e.g. 4999" },
      { key: "priceYear", label: "Price/year ₹", type: "text", placeholder: "e.g. 49999" },
      { key: "maxUsers", label: "Max Users", type: "number", placeholder: "e.g. 25" },
      { key: "trialDays", label: "Trial Days", type: "number", placeholder: "e.g. 14" },
      { key: "features", label: "Features", type: "tags", placeholder: "Type and press Enter" },
      { key: "status", label: "Status", type: "dropdown", options: ["Active", "Inactive", "Coming Soon", "Discontinued"] },
    ],
    priceLabel: "Price",
  },
  custom: {
    key: "custom",
    icon: "ri-settings-3-line",
    title: "Custom",
    subtitle: "Define your own fields",
    inventoryLabel: "Inventory",
    statusOptions: [
      { value: "Active", badge: "bg-green-50 text-green-700" },
      { value: "Inactive", badge: "bg-gray-100 text-gray-600" },
      { value: "Pending", badge: "bg-amber-50 text-amber-700" },
      { value: "Archived", badge: "bg-red-50 text-red-700" },
    ],
    formFields: [],
    inventoryColumns: [
      { key: "itemName", label: "Item Name" },
      { key: "price", label: "Price" },
      { key: "quantity", label: "Quantity" },
      { key: "status", label: "Status" },
    ],
    itemFields: [
      { key: "itemName", label: "Item Name", type: "text", required: true, placeholder: "e.g. Consulting Session" },
      { key: "price", label: "Price ₹", type: "text", placeholder: "e.g. 5000" },
      { key: "quantity", label: "Quantity", type: "number", placeholder: "e.g. 100" },
      { key: "status", label: "Status", type: "dropdown", options: ["Active", "Inactive", "Pending", "Archived"] },
      { key: "notes", label: "Notes", type: "text", placeholder: "Additional details" },
    ],
    priceLabel: "Price",
  },
};

export interface Project {
  id: string;
  name: string;
  category: ProjectCategory;
  sellingTypeId?: string;
  status: string;
  color: string;
  description: string;
  leads: number;
  interested: number;
  booked: number;
  progress: number;
  customFields: Record<string, string>;
}

export interface InventoryItem {
  id: string;
  [key: string]: string;
}

export const projectsData: Project[] = [
  {
    id: "11111111-1111-4bbb-bbbb-111111111111",
    name: "Bandra West Complex",
    category: "properties",
    sellingTypeId: "11111111-1111-4111-a111-111111111111",
    status: "Active",
    color: "indigo",
    description: "Premium residential complex in Mumbai's most sought-after neighborhood. Featuring spacious 2BHK and 3BHK apartments with modern finishes, panoramic sea views, and world-class amenities. Located just 5 minutes from Bandra Station and the upcoming metro line. Each unit is meticulously designed by award-winning architects with imported Italian marble, modular kitchens, and smart home automation.",
    leads: 234,
    interested: 43,
    booked: 16,
    progress: 33,
    customFields: {
      location: "Bandra West, Mumbai",
      priceFrom: "₹1.8 Cr",
      priceTo: "₹2.4 Cr",
      totalUnits: "48",
      reraNumber: "P51800045678",
      possession: "December 2026",
    },
  },
  {
    id: "22222222-2222-4bbb-bbbb-222222222222",
    name: "Industrial Pump Series X",
    category: "products",
    sellingTypeId: "55555555-5555-4555-a555-555555555555",
    status: "Active",
    color: "green",
    description: "High-performance industrial pump series designed for manufacturing and chemical processing plants. Engineered with corrosion-resistant materials and energy-efficient motors, the Series X delivers reliable fluid handling across demanding industrial environments. Available in multiple configurations with flow rates from 50 to 5000 LPM.",
    leads: 156,
    interested: 28,
    booked: 18,
    progress: 25,
    customFields: {
      productCategory: "Industrial Equipment",
      totalSKUs: "8",
      priceFrom: "₹45,000",
      priceTo: "₹2,80,000",
      moq: "5",
      leadTime: "7-10 days",
    },
  },
  {
    id: "33333333-3333-4bbb-bbbb-333333333333",
    name: "MBA Batch July 2026",
    category: "courses",
    sellingTypeId: "44444444-4444-4444-a444-444444444444",
    status: "Active",
    color: "amber",
    description: "A comprehensive 2-year MBA program designed for working professionals and fresh graduates. The curriculum covers General Management, Finance, Marketing, Operations, and Entrepreneurship. Taught by industry veterans and visiting faculty from top B-schools. Includes campus placement assistance and internship opportunities with Fortune 500 companies.",
    leads: 189,
    interested: 37,
    booked: 37,
    progress: 62,
    customFields: {
      courseName: "MBA General Management",
      totalSeats: "60",
      fee: "₹2,40,000",
      startDate: "2026-07-15",
      endDate: "2028-06-30",
      mode: "Offline",
    },
  },
  {
    id: "44444444-4444-4bbb-bbbb-444444444444",
    name: "CRM Pro Software",
    category: "plans",
    sellingTypeId: "77777777-7777-4777-a777-777777777777",
    status: "Active",
    color: "purple",
    description: "A powerful CRM platform built for sales teams to manage leads, automate follow-ups, and close deals faster. Features include lead scoring, pipeline management, WhatsApp integration, and advanced analytics. Trusted by 500+ businesses across India with 99.9% uptime SLA. Available as cloud-hosted or on-premise deployment.",
    leads: 89,
    interested: 12,
    booked: 34,
    progress: 68,
    customFields: {
      planTier: "Basic, Professional, Enterprise",
      priceMonth: "₹999",
      priceYear: "₹9,999",
      maxUsers: "50",
      trialDays: "14",
    },
  },
  {
    id: "55555555-5555-4bbb-bbbb-555555555555",
    name: "Wellness Packages 2026",
    category: "packages",
    sellingTypeId: "66666666-6666-4666-a666-666666666666",
    status: "Active",
    color: "pink",
    description: "Comprehensive health and wellness packages designed for corporates, families, and individuals. From basic annual health checkups to premium executive wellness programs, each package is curated by certified medical professionals. Includes teleconsultation, diagnostic reports, and personalized health coaching.",
    leads: 179,
    interested: 22,
    booked: 18,
    progress: 30,
    customFields: {
      serviceCategory: "Health Checkup & Wellness",
      priceFrom: "₹1,500",
      priceTo: "₹25,000",
      duration: "30-90 mins",
      slotsAvailable: "120",
      validity: "1 year",
    },
  },
  {
    id: "66666666-6666-4bbb-bbbb-666666666666",
    name: "Consulting Services Hub",
    category: "custom",
    sellingTypeId: "88888888-8888-4888-a888-888888888888",
    status: "Active",
    color: "teal",
    description: "A flexible consulting service line for business strategy, digital transformation, and operational excellence. Each engagement is tailored to the client's needs with custom deliverables, timelines, and pricing. Our consultants bring deep industry expertise across manufacturing, fintech, and healthcare sectors to drive measurable outcomes.",
    leads: 98,
    interested: 14,
    booked: 7,
    progress: 18,
    customFields: {},
  },
];

export const inventoryData: Record<string, InventoryItem[]> = {
  "11111111-1111-4bbb-bbbb-111111111111": [
    { id: "cccccccc-0001-4ccc-cccc-000000000001", unitNo: "A-101", type: "2BHK", floor: "1st", area: "950 sqft", price: "₹1.8 Cr", status: "Available", buyer: "—" },
    { id: "cccccccc-0002-4ccc-cccc-000000000002", unitNo: "A-102", type: "2BHK", floor: "1st", area: "950 sqft", price: "₹1.85 Cr", status: "Booked", buyer: "Vikram Mehta", leadName: "Vikram Mehta" },
    { id: "cccccccc-0003-4ccc-cccc-000000000003", unitNo: "A-103", type: "2BHK", floor: "1st", area: "975 sqft", price: "₹1.9 Cr", status: "Available", buyer: "—" },
    { id: "cccccccc-0004-4ccc-cccc-000000000004", unitNo: "B-201", type: "3BHK", floor: "2nd", area: "1350 sqft", price: "₹2.1 Cr", status: "Sold", buyer: "Kavita Joshi", leadName: "Kavita Joshi" },
    { id: "cccccccc-0005-4ccc-cccc-000000000005", unitNo: "B-202", type: "3BHK", floor: "2nd", area: "1350 sqft", price: "₹2.15 Cr", status: "Available", buyer: "—" },
    { id: "cccccccc-0006-4ccc-cccc-000000000006", unitNo: "C-301", type: "2BHK", floor: "3rd", area: "950 sqft", price: "₹1.95 Cr", status: "On Hold", buyer: "Sunita Rao" },
    { id: "cccccccc-0007-4ccc-cccc-000000000007", unitNo: "C-302", type: "2BHK", floor: "3rd", area: "950 sqft", price: "₹1.9 Cr", status: "Available", buyer: "—" },
    { id: "cccccccc-0008-4ccc-cccc-000000000008", unitNo: "D-401", type: "3BHK", floor: "4th", area: "1400 sqft", price: "₹2.2 Cr", status: "Booked", buyer: "Rahul Verma", leadName: "Rahul Verma" },
    { id: "cccccccc-0009-4ccc-cccc-000000000009", unitNo: "D-402", type: "3BHK", floor: "4th", area: "1400 sqft", price: "₹2.2 Cr", status: "Available", buyer: "—" },
    { id: "cccccccc-0010-4ccc-cccc-000000000010", unitNo: "E-501", type: "2BHK", floor: "5th", area: "950 sqft", price: "₹2.0 Cr", status: "Available", buyer: "—" },
    { id: "cccccccc-0011-4ccc-cccc-000000000011", unitNo: "E-502", type: "2BHK", floor: "5th", area: "975 sqft", price: "₹2.05 Cr", status: "Sold", buyer: "Deepika Singh", leadName: "Deepika Singh" },
    { id: "cccccccc-0012-4ccc-cccc-000000000012", unitNo: "F-601", type: "3BHK", floor: "6th", area: "1425 sqft", price: "₹2.25 Cr", status: "Available", buyer: "—" },
  ],
  "22222222-2222-4bbb-bbbb-222222222222": [
    { id: "cccccccc-0021-4ccc-cccc-000000000021", sku: "IPX-450-A", productName: "Centrifugal Pump 450", category: "Centrifugal", stockQty: "120", unitPrice: "₹45,000", status: "In Stock" },
    { id: "cccccccc-0022-4ccc-cccc-000000000022", sku: "IPX-750-B", productName: "Submersible Pump 750", category: "Submersible", stockQty: "85", unitPrice: "₹78,500", status: "In Stock" },
    { id: "cccccccc-0023-4ccc-cccc-000000000023", sku: "IPX-1500-C", productName: "Industrial Pump 1500", category: "Centrifugal", stockQty: "3", unitPrice: "₹1,25,000", status: "Low Stock" },
    { id: "cccccccc-0024-4ccc-cccc-000000000024", sku: "IPX-300-D", productName: "Diaphragm Pump 300", category: "Diaphragm", stockQty: "0", unitPrice: "₹58,000", status: "Out of Stock" },
    { id: "cccccccc-0025-4ccc-cccc-000000000025", sku: "IPX-5000-E", productName: "Heavy Duty Pump 5000", category: "Centrifugal", stockQty: "4", unitPrice: "₹2,80,000", status: "Low Stock" },
    { id: "cccccccc-0026-4ccc-cccc-000000000026", sku: "IPX-200-F", productName: "Gear Pump 200", category: "Gear", stockQty: "250", unitPrice: "₹32,000", status: "In Stock" },
    { id: "cccccccc-0027-4ccc-cccc-000000000027", sku: "IPX-800-G", productName: "Vacuum Pump 800", category: "Vacuum", stockQty: "0", unitPrice: "₹95,000", status: "Out of Stock" },
  ],
  "33333333-3333-4bbb-bbbb-333333333333": [
    { id: "cccccccc-0031-4ccc-cccc-000000000031", batchNo: "MBA-JUL-01", course: "MBA General Management", mode: "Offline", seats: "30", enrolled: "23", fee: "₹2,40,000", startDate: "15 Jul 2026", status: "Enrolled" },
    { id: "cccccccc-0032-4ccc-cccc-000000000032", batchNo: "MBA-JUL-02", course: "MBA General Management", mode: "Online", seats: "30", enrolled: "14", fee: "₹1,80,000", startDate: "20 Jul 2026", status: "Available" },
    { id: "cccccccc-0033-4ccc-cccc-000000000033", batchNo: "MBA-FIN-01", course: "MBA Finance", mode: "Offline", seats: "25", enrolled: "0", fee: "₹2,40,000", startDate: "01 Aug 2026", status: "Available" },
    { id: "cccccccc-0034-4ccc-cccc-000000000034", batchNo: "MBA-MKT-01", course: "MBA Marketing", mode: "Hybrid", seats: "20", enrolled: "0", fee: "₹2,20,000", startDate: "15 Aug 2026", status: "Available" },
    { id: "cccccccc-0035-4ccc-cccc-000000000035", batchNo: "EXEC-01", course: "Executive MBA", mode: "Online", seats: "15", enrolled: "12", fee: "₹3,50,000", startDate: "01 Sep 2026", status: "Waitlist" },
  ],
  "44444444-4444-4bbb-bbbb-444444444444": [
    { id: "cccccccc-0041-4ccc-cccc-000000000041", planName: "Basic", priceMonth: "₹999/month", priceYear: "₹9,999/year", maxUsers: "5", activeClients: "18", status: "Active" },
    { id: "cccccccc-0042-4ccc-cccc-000000000042", planName: "Professional", priceMonth: "₹4,999/month", priceYear: "₹49,999/year", maxUsers: "25", activeClients: "12", status: "Active" },
    { id: "cccccccc-0043-4ccc-cccc-000000000043", planName: "Enterprise", priceMonth: "₹9,999/month", priceYear: "₹99,999/year", maxUsers: "50", activeClients: "4", status: "Active" },
  ],
  "55555555-5555-4bbb-bbbb-555555555555": [
    { id: "cccccccc-0051-4ccc-cccc-000000000051", packageName: "Basic Health Check", duration: "30 mins", price: "₹1,500", slotsAvailable: "40", status: "Available" },
    { id: "cccccccc-0052-4ccc-cccc-000000000052", packageName: "Executive Wellness", duration: "60 mins", price: "₹5,000", slotsAvailable: "25", status: "Available" },
    { id: "cccccccc-0053-4ccc-cccc-000000000053", packageName: "Family Health Package", duration: "90 mins", price: "₹12,000", slotsAvailable: "15", status: "Booked" },
    { id: "cccccccc-0054-4ccc-cccc-000000000054", packageName: "Cardiac Screening", duration: "45 mins", price: "₹8,500", slotsAvailable: "20", status: "Available" },
    { id: "cccccccc-0055-4ccc-cccc-000000000055", packageName: "Full Body Platinum", duration: "90 mins", price: "₹25,000", slotsAvailable: "10", status: "Available" },
    { id: "cccccccc-0056-4ccc-cccc-000000000056", packageName: "Diabetes Care Plan", duration: "45 mins", price: "₹3,500", slotsAvailable: "10", status: "Discontinued" },
  ],
  "66666666-6666-4bbb-bbbb-666666666666": [
    { id: "cccccccc-0061-4ccc-cccc-000000000061", itemName: "Strategy Audit — Fintech", price: "₹1,50,000", quantity: "3", status: "Active", notes: "2-week engagement" },
    { id: "cccccccc-0062-4ccc-cccc-000000000062", itemName: "Digital Transformation Roadmap", price: "₹3,20,000", quantity: "5", status: "Active", notes: "4-week engagement" },
    { id: "cccccccc-0063-4ccc-cccc-000000000063", itemName: "Operational Excellence Review", price: "₹1,80,000", quantity: "0", status: "Archived", notes: "Discontinued service" },
    { id: "cccccccc-0064-4ccc-cccc-000000000064", itemName: "Fractional CTO Retainer", price: "₹2,50,000", quantity: "2", status: "Active", notes: "Monthly retainer" },
    { id: "cccccccc-0065-4ccc-cccc-000000000065", itemName: "Market Entry Assessment", price: "₹4,00,000", quantity: "4", status: "Pending", notes: "6-week engagement" },
  ],
};

export const projectStats = {
  active: 6,
  totalLeads: 945,
  industriesCovered: 6,
  avgConversion: 18.6,
};

export const projectColorOptions = ["indigo", "green", "amber", "purple", "blue", "pink", "teal", "red"];

export function getStatusBadge(status: string, category: ProjectCategory): string {
  const config = categoryConfigs[category];
  const found = config.statusOptions.find((s) => s.value === status);
  return found?.badge || "bg-gray-100 text-gray-600";
}

export function getColorClass(color: string): string {
  const map: Record<string, string> = {
    indigo: "bg-indigo-500",
    green: "bg-green-500",
    amber: "bg-amber-500",
    purple: "bg-purple-500",
    blue: "bg-blue-500",
    pink: "bg-pink-500",
    teal: "bg-teal-500",
    red: "bg-red-500",
  };
  return map[color] || "bg-indigo-500";
}

export function getInventoryLabel(category: ProjectCategory): string {
  return categoryConfigs[category].inventoryLabel;
}